// src/components/NotificationBell.jsx
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Package, 
  RotateCcw,
  CheckCheck,
  Clock,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import  {API_URL} from '../Config';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadLatest = async () => {
    try {
      const [countRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/notifications/unread-count`, { headers }),
        axios.get(`${API_URL}/notifications/latest?limit=8`, { headers })
      ]);
      setUnreadCount(countRes.data.data.count);
      setNotifications(notifRes.data.data);
    } catch (err) {
      console.error('Erreur notifications');
    }
  };

  useEffect(() => {
    loadLatest();
    const interval = setInterval(loadLatest, 15000);
    return () => clearInterval(interval);
  }, []);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/notifications/${id}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {}
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/mark-all-read`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {}
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'demande_created': return <FileText className="w-4 h-4" />;
      case 'demande_validated': return <CheckCircle className="w-4 h-4" />;
      case 'demande_refused': return <XCircle className="w-4 h-4" />;
      case 'demande_ready': return <Package className="w-4 h-4" />;
      case 'stock_low': return <AlertTriangle className="w-4 h-4" />;
      case 'materiel_returned': return <RotateCcw className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'demande_created': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      case 'demande_validated': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'demande_refused': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'demande_ready': return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';
      case 'stock_low': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20';
      case 'materiel_returned': return 'text-purple-600 bg-purple-50 dark:bg-purple-900/20';
      default: return 'text-gray-600  dark:bg-gray-800';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Cloche */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg  dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
              </div>
              {unreadCount > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center gap-1"
                >
                  <CheckCheck className="w-4 h-4" />
                  Tout lire
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400">Aucune notification</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Les nouvelles notifications apparaîtront ici</p>
              </div>
            ) : (
              <div className="divide-y dark:divide-gray-700">
                {notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`p-4 hover: dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${
                      !notif.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                    }`}
                    onClick={() => markAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-lg ${getNotificationColor(notif.type)}`}>
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {notif.title}
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1 line-clamp-2">
                          {notif.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(notif.created_at), { 
                              addSuffix: true, 
                              locale: fr 
                            })}
                          </span>
                          {!notif.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t dark:border-gray-700  dark:bg-gray-800">
            <Link 
              to="/notifications" 
              className="block text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={() => setOpen(false)}
            >
              Voir toutes les notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}